import React, { createContext, useContext, useReducer, useRef } from "react";
import { ChartMessageItem, MessageAuditStatus, MessageMaskType, MessageType } from "../constants/types";
import { Group } from "../../imsdk/db/t_group";
import { GroupMember } from "../../imsdk/db/t_group_member";
import { User } from "../../imsdk/db/t_user";

interface ChatGroupUpdate {
    group?: Group;
    isAdmin?: boolean;
    adminSet?: Set<number>;
    groupNotice?: string;
    groupNoticeRead?: string;
    inGroupRemark?: string; //自己在群的昵称
    users?: (GroupMember | User)[];
}

type Payload =
    | { type: "CHAT_CLEAN_MSG"; payload: { tid: string } }
    | { type: "CHAT_GROUP_UPDATE"; payload: ChatGroupUpdate }
    | { type: "CHAT_UPDATE_REMARK"; payload: string }
    | { type: "UPDATE_NOTICE_READ"; payload: string }
    | {
          type: "UPDATE_JOIN_GROUP_USER";
          payload?: { users: (GroupMember | User)[]; groupRequests: any[]; forTid?: string };
      }
    | { type: "CHAT_CLEAN"; payload?: any }
    /** 进入新会话：重置与上一会话绑定的 chat 侧状态，避免入群申请等 UI 残留 */
    | { type: "CHAT_SWITCH_TOPIC"; payload: TopicWithSummary }
    | { type: "CHAT_UPDATE_TOPIC"; payload: TopicWithSummary }
    | { type: "UPDATE_CHAT_USER"; payload: (GroupMember | User)[] }
    | { type: "UPDATE_GROUP_REMARK"; payload: string }
    | { type: "UPDATE_GROUP_NAME"; payload: string };

import { TopicWithSummary } from "../../imsdk/api/topic";
import { beforeDatePlus, isGroupTopic } from "../utils/utils";
import * as ImSdk from "../../imsdk/api";
import { joinGroupList, report } from "../net/service";
import { UserSchema } from "../realm/contactSchema";
import { Msg } from "../../imsdk/db/t_msg";
import { Topic } from "../../imsdk/db/t_topic";
import { parseSingleOther } from "../utils/helper";
import { getUid } from "./token";
import { uploadImageManager } from "../utils/uploadImage";
import { MessageSchema } from "../realm/schema";

interface ChatState {
    isAdmin?: boolean; //是否是管理员
    groupNotice?: string; //
    groupNoticeRead?: string; //
    inGroupRemark?: string; //
    joinGroupUsers?: { users: (GroupMember | User)[]; groupRequests: any[] };
    adminSet: Set<number>;
    curTopicWithSummary: TopicWithSummary;
    cleanTid?: string; //需要清空聊天的tid
    dataVersion: number; // 当前最新的版本
}

const initState: ChatState = {
    curTopicWithSummary: {} as TopicWithSummary,
    adminSet: new Set<number>([]),
    dataVersion: 0,
};

const reducer = (state: ChatState, { type, payload }: Payload): ChatState => {
    switch (type) {
        case "CHAT_UPDATE_REMARK":
            const topicWithSummary = state.curTopicWithSummary;
            topicWithSummary.topic.remark = payload;
            const g = topicWithSummary.group;
            if (g) {
                g.remark = payload;
                topicWithSummary.group = g;
            }
            return { ...state, curTopicWithSummary: topicWithSummary, dataVersion: state.dataVersion + 1 };
        case "CHAT_UPDATE_TOPIC":
            return { ...state, curTopicWithSummary: payload, dataVersion: state.dataVersion + 1 };
        case "CHAT_GROUP_UPDATE":
            return {
                ...state,
                ...payload,
                curTopicWithSummary: { ...state.curTopicWithSummary, group: payload.group, users: payload.users },
                dataVersion: state.dataVersion + 1,
            };
        case "UPDATE_JOIN_GROUP_USER": {
            if (!payload) {
                return state;
            }
            if (
                payload.forTid != null &&
                payload.forTid !== state.curTopicWithSummary?.topic?.tid
            ) {
                return state;
            }
            const { forTid: _forTid, ...joinGroupUsers } = payload;
            return { ...state, joinGroupUsers, dataVersion: state.dataVersion + 1 };
        }
        case "UPDATE_NOTICE_READ":
            return { ...state, groupNoticeRead: payload, dataVersion: state.dataVersion + 1 };
        case "CHAT_CLEAN_MSG":
            return { ...state, cleanTid: payload.tid, dataVersion: state.dataVersion + 1 };
        case "UPDATE_CHAT_USER":
            const curTopicWithSummary = state.curTopicWithSummary;
            curTopicWithSummary.users = payload;
            return { ...state, curTopicWithSummary: curTopicWithSummary, dataVersion: state.dataVersion + 1 };
        case "UPDATE_GROUP_NAME":
            let group = state.curTopicWithSummary.group;
            if (group) {
                group.name = payload;
            }
            const topic = { ...state.curTopicWithSummary.topic, name: payload };
            return {
                ...state,
                curTopicWithSummary: { ...state.curTopicWithSummary, topic: topic, group: group },
                dataVersion: state.dataVersion + 1,
            };
        case "UPDATE_GROUP_REMARK":
            const users = state.curTopicWithSummary.users;
            users?.forEach((v) => {
                if (v.uid === ImSdk.getInstance().CurrentUid()) {
                    v.remark = payload;
                }
            });
            return {
                ...state,
                curTopicWithSummary: { ...state.curTopicWithSummary, users: users },
                inGroupRemark: payload,
                dataVersion: state.dataVersion + 1,
            };
        case "CHAT_SWITCH_TOPIC":
            return {
                ...initState,
                curTopicWithSummary: payload,
                dataVersion: state.dataVersion + 1,
            };
        case "CHAT_CLEAN":
            return { ...initState, dataVersion: state.dataVersion + 1 };
        default:
            return { ...state };
    }
};

// 消息存入数据库
export async function upsetMsg(msg: ChartMessageItem) {
    const message = msg as any as Msg;
    const uid = ImSdk.getInstance().CurrentUid();
    await ImSdk.getInstance().UpsetMsg(uid, message);
}

export async function delMsgByCid(tid: string, cid: number, mid: string, sender: number) {
    return await ImSdk.getInstance().DelMsgByCid(tid, cid, mid, sender);
}

export function GetJoinGroupList(
    state: ChatState,
    chatDispatch: React.Dispatch<Payload>,
    /** 从 navigate 的 setTimeout 里调用时传入，避免闭包里的 state 仍是上一会话 */
    tidOverride?: string,
) {
    const topicWithSummary = state.curTopicWithSummary;
    const tid = tidOverride ?? topicWithSummary?.topic?.tid ?? "";
    if (!tid || !isGroupTopic(tid)) {
        return;
    }
    joinGroupList({ tid, type: 0 }).then((res) => {
        if (res.code === 0) {
            chatDispatch({
                type: "UPDATE_JOIN_GROUP_USER",
                payload: {
                    users: res.data.users,
                    groupRequests: res.data.groupRequests,
                    forTid: tid,
                },
            });
        }
    });
}

export function getGroupDetail(tid: string, chatDispatch: React.Dispatch<Payload>) {
    const im = ImSdk.getInstance();
    if (isGroupTopic(tid)) {
        im.GetGroupDetail(tid).then((res) => {
            if (res.group && res.members) {
                processGroupData(res, chatDispatch);
            }
        });
    } else {
        const uid = parseSingleOther(tid);
        im.GetUserMuti([uid, getUid()]).then((r) => {
            const users = r.users || [];
            chatDispatch({
                type: "CHAT_GROUP_UPDATE",
                payload: { users: users },
            });
        });
    }
}

export function updateDetail(state: ChatState, chatDispatch: React.Dispatch<Payload>) {
    const topicWithSummary = state.curTopicWithSummary;
    const tid = topicWithSummary?.topic?.tid;
    if (tid && isGroupTopic(tid)) {
        reload(tid, chatDispatch, state);
    } else {
        const im = ImSdk.getInstance();
        im.GetTopicDetail(tid).then((r) => {
            if (r && r.topic) {
                chatDispatch({ type: "CHAT_UPDATE_TOPIC", payload: r.topic });
            }
        });
    }
}

export function reload(tid: string, chatDispatch: React.Dispatch<Payload>, state?: ChatState) {
    const im = ImSdk.getInstance();
    im.GetGroupRemote(tid, state?.curTopicWithSummary?.group).then((res) => {
        processGroupData(res, chatDispatch);
    });
}

export function processGroupData(res: { group?: Group; members?: (GroupMember | User)[] }, chatDispatch: React.Dispatch<Payload>) {
    const im = ImSdk.getInstance();
    let isAdmin = false;
    const group = res.group;
    if (group?.adminUids) {
        const curUid = im.CurrentUid();
        if (curUid != 0 && group.adminUids.includes(curUid)) {
            isAdmin = true;
        }
    }
    const data: ChatGroupUpdate = { isAdmin: isAdmin };
    if (group) {
        data.group = group;
        const aSet = new Set(group.adminUids);
        aSet.add(group.uid);
        data.groupNotice = group.notice;
        data.groupNoticeRead = group.notice_read;

        const membersUids = res.members || [];
        if (membersUids && membersUids.length > 0) {
            const compare = (a: GroupMember | User, b: GroupMember | User): number => {
                // 群主最大
                if (a.uid === group?.uid) {
                    return 1;
                }
                if (b.uid === group?.uid) {
                    return -1;
                }
                // 管理员最大
                if (aSet.has(a.uid) && !aSet.has(b.uid)) {
                    return 1;
                }
                if (!aSet.has(a.uid) && aSet.has(b.uid)) {
                    return -1;
                }
                // 按姓名或者昵称排序
                const aname = a.remark || a.nick || a.name || "";
                const bname = b.remark || b.nick || b.name || "";
                if (aname > bname) {
                    return -1;
                }
                return 1;
            };
            membersUids.sort((a, b): number => {
                return -compare(a, b);
            });
            data.users = membersUids;
            membersUids.forEach((v) => {
                if (v.uid === im.CurrentUid()) {
                    data.inGroupRemark = v.remark;
                }
            });
        }
        data.adminSet = aSet;
    }
    chatDispatch({
        type: "CHAT_GROUP_UPDATE",
        payload: data,
    });

    console.log(
        `====群id${res?.group?.tid}群主是${res?.group?.uid}管理员有${Array.from(data.adminSet ?? [])}我${isAdmin ? "是" : "不是"
        }管理员 是否在群 ---- ${group?.flag}`
    );
}

export const fix_msgs_time_all = (msgs: ChartMessageItem[], fixSendState: boolean = false): ChartMessageItem[] => {
    // msgs[0] 最新消息
    // msgs[1] 历史消息
    let lasttime = 0;
    const n = msgs?.length || 0;
    const newmsgs: ChartMessageItem[] = [];
    for (let i = n - 1; i >= 0; i--) {
        const msg = msgs[i];
        if (fixSendState && msg.send_statue === "0") {
            let update = false;
            if (msg.type === MessageType.IMAGE || msg.type === MessageType.VIDEO) {
                const k = `${msg.sender}-${msg.cid}-${msg.upload_path}`;
                //如果查询到的消息还是发送中则对该消息进行修复
                if (!uploadImageManager.isUpload(k)) {
                    msg.send_statue = "1";
                    update = true;
                }
            } else {
                if (msgs[i].mid && msgs[i].mid.length > 0) {
                    msgs[i].send_statue = "";
                    update = true;
                } else {
                    update = true;
                    msg.send_statue = "1";
                }
            }
            if (update) {
                upsetMsg(msgs[i]).then();
            }
        }

        if (msg.mask === MessageMaskType.DELETED) {
            ImSdk.getInstance()
                .DelMsg(msg.tid, [{ mid: msg.mid, cid: msg.cid }])
                .then();
            continue;
        }

        if (msg.type === MessageType.LOCAL_TIME) {
            continue;
        }
        // 添加时间节点处理
        if (msg.stime - lasttime > 10 * 60 * 1000) {
            lasttime = msg.stime;
            const a = Object.assign({}, msg);
            a.type = MessageType.LOCAL_TIME;
            a.content = beforeDatePlus(a.stime);
            a.stime--;
            newmsgs.push(a);
        }
        const b = Object.assign({}, msg);
        newmsgs.push(b);
    }

    // 改为根据发送时间排序,不在相信原有的排序
    // return newmsgs.sort((a: ChartMessageItem, b: ChartMessageItem) => {
    //     const timeA = a.stime || 0;
    //     const timeB = b.stime || 0;
    //     return timeB - timeA;
    // })

    return newmsgs.reverse();
};

// 首次进入会话只拉一页较大窗口（100 条），后续上滑补历史使用小页，减少单次请求负担。
const BRIDGE_FETCH_PAGE_SIZE_FIRST = 100;
const BRIDGE_FETCH_PAGE_SIZE_MORE = 30;

async function fetchBridgePage(
    tid: string,
    topic: Topic,
    forceLatest: boolean,
    pageSize: number = BRIDGE_FETCH_PAGE_SIZE_MORE,
): Promise<{ error?: any; reachedBridge?: boolean; hasMoreRemote?: boolean }> {
    // 消息桥接：批量后先远端分页拉历史，直到“远端页中出现本地已有 mid”再切回纯本地。
    const im = ImSdk.getInstance();
    const now = Date.now();
    const pages = topic.bridgePages || 0;

    // 首次/强制场景优先拉最新一页；后续使用 bridgeCursor 继续向更旧历史翻页。
    const useCursor = !forceLatest && !!topic.bridgeCursor && topic.bridgeCursor.length > 0;
    const body = useCursor
        ? {
              tid,
              size: pageSize,
              maxMsgId: topic.bridgeCursor,
              containMax: false,
          }
        : { tid, size: pageSize, containMax: true };

    const r = await im.QryMsgRemote(tid, body);
    if (r.error) {
        return { error: r.error };
    }

    // overlap=true 表示本页与本地消息“接上了”；oldestMid 作为下一页桥接游标。
    const msgs = r.msgs || [];
    const hasMsgs = msgs.length > 0;
    const overlap = r.overlap === true;
    const oldestMid = r.oldestMid || (hasMsgs ? msgs[msgs.length - 1].mid : "");
    const reachedBridge = overlap || !hasMsgs;
    const nextPages = pages + 1;

    if (reachedBridge) {
        // 已接上（或远端无更多）：标记 msg_synced=1，后续会话历史走本地 DB。
        await im.UpdateTopicBridgeState(tid, {
            msg_synced: 1,
            bridgeState: 2,
            bridgeCursor: "",
            bridgePages: 0,
            bridgeUpdatedAt: now,
        });
        await im.UpdateMsgSynced(tid);
        topic.msg_synced = 1;
        topic.bridgeState = 2;
        topic.bridgeCursor = "";
        topic.bridgePages = 0;
        topic.bridgeUpdatedAt = now;
    } else {
        // 还未接上：保存游标与分页计数，下次继续桥接。
        await im.UpdateTopicBridgeState(tid, {
            msg_synced: 0,
            bridgeState: 1,
            bridgeCursor: oldestMid || "",
            bridgePages: nextPages,
            bridgeUpdatedAt: now,
        });
        topic.msg_synced = 0;
        topic.bridgeState = 1;
        topic.bridgeCursor = oldestMid || "";
        topic.bridgePages = nextPages;
        topic.bridgeUpdatedAt = now;
    }
    return { reachedBridge, hasMoreRemote: hasMsgs };
}

/** 上滑历史时按需桥接一页：本地无更多且未接上时调用 */
export async function bridgeOnePageIfNeeded(
    tid: string,
    forceLatest: boolean = false,
): Promise<{ done: boolean; continued: boolean; error?: any }> {
    const im = ImSdk.getInstance();
    const detail = await im.GetTopicDetail(tid);
    const topic = detail.topic?.topic;
    if (!topic) {
        return { done: true, continued: false };
    }
    if ((topic.msg_synced ?? 0) === 1) {
        return { done: true, continued: false };
    }
    const rst = await fetchBridgePage(tid, topic, forceLatest, BRIDGE_FETCH_PAGE_SIZE_MORE);
    if (rst.error) {
        return { done: false, continued: false, error: rst.error };
    }
    const done = !!rst.reachedBridge || !rst.hasMoreRemote;
    const continued = !!rst.hasMoreRemote && !rst.reachedBridge;
    return { done, continued };
}

export async function loadNewMsg(tid: string, cur: ChartMessageItem[]): Promise<ChartMessageItem[]> {
    let list = [...cur];
    const r = await ImSdk.getInstance().GetMsg({
        tid: tid,
        order: "desc",
        limit: 30,
        // 不能用当前的mid去查询,因为在下面这种场景下会发生不显示的情况
        // A和B几乎同时发送，B比A稍晚一点，就是A发送后，B在还没收到新消息的同步的时候发出。
        // B发出请求后台成功了并且更新了mid以后，此时A发的消息同步进来。
        // 但是事实上，B的mid是大于A的mid的，而查询的时候是拿B的mid去查的，因为A的mid比B的小，所以查不到。
        //  rangeMinMid:
        //         cur && cur.length > 0
        //             ? cur[0].mid
        //             : undefined,
        // contain: true,
        // cid: cid
    });
    if (r && r.msgs && r.msgs.length > 0) {
        for (const msg of r.msgs) {
            const newMsg = msg as ChartMessageItem;
            if (newMsg.mid) {
                const idxByMid = list.findIndex((v) => v.mid === newMsg.mid);
                if (idxByMid >= 0) {
                    newMsg.user = list[idxByMid].user;
                    list[idxByMid] = newMsg;
                    continue;
                }
            }
            // 修复：使用 list 而不是 cur，因为 list 可能已经通过 mid 匹配更新过了
            // 如果使用 cur，可能会找不到已经在 list 中更新的消息，导致重复添加
            const index = list.findIndex((v) => v.tid === msg.tid && v.cid === msg.cid && v.sender === msg.sender);
            if (index >= 0) {
                newMsg.user = list[index].user;
                list[index] = newMsg;
                continue;
            }
            list.push(newMsg);
        }
    }
    list = sortMessage(list);
    const nnnmsgs: ChartMessageItem[] = fix_msgs_time_all(list);
    return nnnmsgs;
}

/**
 * 按真实时间排序，消息永不乱序
 */
export function sortMessage(list: ChartMessageItem[]) {
    return list.sort((a, b) => {
        const hasMidA = !!a.mid;
        const hasMidB = !!b.mid;

        // ① 双方都有 mid → 用 mid 排序（字典序即可）
        if (hasMidA && hasMidB) {
            return a.mid > b.mid ? -1 : 1;
        }

        // ② 一边有 mid 一边没有 → 有 mid 的优先（位置固定）
        if (hasMidA !== hasMidB) {
            return hasMidA ? -1 : 1;
        }

        // ③ 双方都没有 mid（发送中消息） → 用 cid 排序（时间戳）
        if (!hasMidA && !hasMidB) {
            return (a.cid || 0) - (b.cid || 0);
        }

        // ④ 都有 mid 但 mid 相等 → stime 决定
        if (a.stime !== b.stime) {
            return a.stime - b.stime;
        }

        // ⑤ 最后兜底
        return (a.cid || 0) - (b.cid || 0);
    });
}

export async function loadMsg(topicSummary: TopicWithSummary): Promise<ChartMessageItem[]> {
    let messageList: ChartMessageItem[] = [];
    const tid = topicSummary.topic.tid;
    const topic = topicSummary.topic;
    let newmsgs: ChartMessageItem[] = [];
    const im = ImSdk.getInstance();
    if ((topic.msg_synced ?? 0) !== 1) {
        // 批量后首次进入优先从远端拉最新窗口，避免长时间离线时先展示旧本地消息。
        await fetchBridgePage(tid, topic, true, BRIDGE_FETCH_PAGE_SIZE_FIRST);
        const reload = await im.GetMsg({
            tid: tid,
            order: "desc",
            limit: 30,
        });
        if (reload.msgs && reload.msgs.length > 0) {
            newmsgs = reload.msgs as ChartMessageItem[];
        }
    } else {
        const r = await im.GetMsg({
            tid: tid,
            order: "desc",
            limit: 30,
        });
        if (r.error) {
            report({ "msg": `loadMsg-${r.error}` }).then();
        }
        if (r.msgs && r.msgs.length > 0) {
            newmsgs = r.msgs as ChartMessageItem[];
        }
    }

    if (newmsgs.length === 0) {
        const { msgs } = await im.QryLatest(tid);
        if (msgs && msgs.length > 0) {
            newmsgs = msgs as any as ChartMessageItem[];
        }
    }
    // await fix_msgs_user(tid, newmsgs);
    messageList && newmsgs.push(...messageList);
    messageList = fix_msgs_time_all(newmsgs, true);

    return messageList;
}

export async function loadLocalMsgsFromMsg(
	msg: MessageSchema,
	messageList: ChartMessageItem[],
	isContain: boolean = true,
	limit: number = 15
): Promise<ChartMessageItem[]> {
	const tid = msg.tid;

	const r = await ImSdk.getInstance().GetMsg({
		tid: msg.tid,
		cid: msg.cid,
		contain: isContain,
		order: 'asc',
		limit: limit,
	});
	if (r.error) {
        report({ "msg": `loadMsg-${r.error}` }).then();
		return [];
	} else {
		let newmsgs: ChartMessageItem[] = [];
		if (r.msgs && r.msgs.length > 0) {
			newmsgs = r.msgs.reverse() as ChartMessageItem[];
			if (messageList.length > 0) {
				newmsgs = [...newmsgs, ...messageList];
			} else {
				return newmsgs;
			}
		} else {
			return [];
		}
		return newmsgs;
	}
}

export async function checkMsg(
    tid: string,
    msgs: ChartMessageItem[],
    oldCount: number
): Promise<ChartMessageItem[]> {
    let result = [...msgs];                     // 不修改原 msgs
    let previousCount = oldCount;
    while (true) {
        const goneCount = result.filter(v => v.visible === MessageAuditStatus.GONE).length;
        // 不满足条件 ==> 不再拉取
        if (!(goneCount > 10 && result.length - previousCount >= 30)) {
            break;
        }
        // 找到当前最后一条（最小 cid）
        const lastMsgCid = result[result.length - 1]?.cid ?? 0;

        const r = await ImSdk.getInstance().GetMsg({
            tid,
            order: "desc",
            limit: 30,
            maxCid: lastMsgCid,
            contain: false,
        });

        if (!r.msgs || r.msgs.length === 0) break;

        const list = r.msgs as ChartMessageItem[];

        // 防止重复拉取相同 cid 的消息（有些 IM SDK 会返回重复）
        const exists = new Set(result.map(m => m.cid));
        const filtered = list.filter(m => !exists.has(m.cid));

        if (filtered.length === 0) break;

        result.push(...filtered);

        // 时间修复（注意频率）
        result = fix_msgs_time_all(result);

        previousCount = result.length - filtered.length;
    }

    return result;
}


export async function GetHistoryMsg(tid: string, messageList: ChartMessageItem[], limit: number = 100): Promise<ChartMessageItem[]> {
    let historyMsgs: ChartMessageItem[] = [];
    const reversed = messageList.slice().reverse();
    const lastMsgCid = reversed[0].cid ?? 0
    // const lastServerMsgMid = reversed.find((v) => v.mid && v.mid.length > 0)?.mid || "";
    const r = await ImSdk.getInstance().GetMsg({
        tid: tid,
        order: "desc",
        limit: limit,
        maxCid: lastMsgCid,
        contain: false,
        // rangeMaxMid: lastServerMsgMid,
        // containMaxMid: false, // 不包含当前 mid，往前翻页

        // contain: false,
        // maxCid: messageList && messageList.length > 0 ? messageList[messageList.length - 1].cid : 0,
    });
    if (r.msgs && r.msgs.length > 0) {
        historyMsgs = r.msgs as ChartMessageItem[];
    } else {
        const im = ImSdk.getInstance();
        const detail = await im.GetTopicDetail(tid);
        const topic = detail.topic?.topic;
        if (topic && (topic.msg_synced ?? 0) !== 1) {
            const rst = await fetchBridgePage(tid, topic, false);
            if (!(rst.error || !rst.hasMoreRemote)) {
                const retry = await im.GetMsg({
                    tid,
                    order: "desc",
                    limit: limit,
                    maxCid: lastMsgCid,
                    contain: false,
                });
                if (retry.msgs && retry.msgs.length > 0) {
                    historyMsgs = retry.msgs as ChartMessageItem[];
                }
            }
        } else {
            const lastServerMsgMid = reversed.find((v) => v.mid && v.mid.length > 0)?.mid || "";
            const body = {
                tid: tid,
                reverse: false,
                MaxMsgId: lastServerMsgMid,
                containMax: false,
                size: 30,
            };
            const r1 = await im.QryMsgRemote(tid, body);
            if (r1.msgs && r1.msgs.length > 0) {
                historyMsgs = r1.msgs as any as ChartMessageItem[];
            }
        }
    }
    if (historyMsgs.length) {
        // await fix_msgs_user(tid, historyMsgs);
        const newMsgs = fix_msgs_time_all(historyMsgs);
        messageList.push(...newMsgs);
        return messageList;
    } else {
        return []
    }
}

export const fix_msgs_user1 = async (
    users: (GroupMember | User)[],
    otherUser: { [uid: number]: GroupMember | User },
    msgs: ChartMessageItem[]
) => {
    const length = users.length;
    let userMap: { [uid: number]: GroupMember | User } = {};
    let logoutUser = otherUser;
    for (let i = 0; i < length; i++) {
        const v = users[i];
        userMap[v.uid] = v;
    }
    const n = msgs.length;
    for (let i = 0; i < n; i++) {
        if (msgs[i].type === MessageType.LOCAL_TIME) {
            continue;
        }
        const u = userMap[msgs[i].sender];
        if (u) {
            msgs[i].user = u as any as UserSchema;
        } else {
            //查询退群的用户
            const res = await ImSdk.getInstance().GetUserMuti([msgs[i].sender], true);
            if (res.users && res.users.length > 0) {
                const user = res.users[0];
                userMap[user.uid] = user;
                logoutUser[user.uid] = user;
                msgs[i].user = user;
            }
        }
    }
    return { msgs, logoutUser };
};

export const fix_msgs_user = async (tid: string, msgs: ChartMessageItem[]) => {
    async function fix_user_msg(uids: number[]) {
        const res = await ImSdk.getInstance().GetUserMuti(uids);
        if (!res.error && res.users && res.users.length > 0) {
            let userMap: { [uid: number]: User } = {};
            res.users.forEach((v) => {
                userMap[v.uid] = v;
            });
            const n = msgs.length;
            for (let i = 0; i < n; i++) {
                if (msgs[i].type === MessageType.LOCAL_TIME) {
                    continue;
                }
                const u = userMap[msgs[i].sender];
                if (u) {
                    msgs[i].user = u as any as UserSchema;
                }
            }
        }
    }

    async function fix_group_user_msg() {
        const res = await ImSdk.getInstance().QryGroupMembersDetail(tid);
        // GetGroupDetail
        if (!res.error && res.members && res.members.length > 0) {
            let userMap: { [uid: number]: GroupMember } = {};
            const length = res.members.length;
            for (let i = 0; i < length; i++) {
                const v = res.members[i];
                //如果没有群备注，则去查自己对好友的备注
                if (!v.remark || v.remark.length === 0) {
                    const res = await ImSdk.getInstance().GetUserMuti([v.uid], true);
                    if (res.users && res.users.length > 0) {
                        v.remark = res.users[0].remark;
                    }
                }

                userMap[v.uid] = v;
            }
            const n = msgs.length;
            for (let i = 0; i < n; i++) {
                if (msgs[i].type === MessageType.LOCAL_TIME) {
                    continue;
                }
                const u = userMap[msgs[i].sender];
                if (u) {
                    msgs[i].user = u as any as UserSchema;
                } else {
                    //查询退群的用户
                    const res = await ImSdk.getInstance().GetUserMuti([msgs[i].sender], true);
                    if (res.users && res.users.length > 0) {
                        msgs[i].user = res.users[0];
                    }
                }
            }
        }
    }

    // 填充消息里的用户信息
    if (msgs && msgs.length > 0) {
        if (isGroupTopic(tid)) {
            await fix_group_user_msg();
        } else {
            const uid = parseSingleOther(tid);
            await fix_user_msg([uid, getUid()]);
        }
    }
};

const ChatDispatchContext = createContext<React.Dispatch<Payload>>(() => {});
const ChatStateContext = createContext<ChatState>(initState);
const ChatStateRefContext = createContext<React.MutableRefObject<ChatState>>({
    current: initState,
});

export function ChatProvider({ children }: { children: React.ReactNode }) {
    const [chatState, chatDispatch] = useReducer(reducer, initState);
    const stateRef = useRef(chatState);
    stateRef.current = chatState;
    return (
        <ChatDispatchContext.Provider value={chatDispatch}>
            <ChatStateRefContext.Provider value={stateRef}>
                <ChatStateContext.Provider value={chatState}>{children}</ChatStateContext.Provider>
            </ChatStateRefContext.Provider>
        </ChatDispatchContext.Provider>
    );
}

/** 订阅完整会话状态（ChatList、群设置等需要） */
export const useChatStore = () => {
    return {
        chatState: useContext(ChatStateContext),
        chatDispatch: useContext(ChatDispatchContext),
    };
};

/**
 * 仅拿 dispatch，不因切换会话而重渲染。
 * 用于会话列表页等：从通讯录进聊天时避免整表 FlatList 同步重绘卡顿。
 */
export const useChatDispatchOnly = () => useContext(ChatDispatchContext);

/** 在 setTimeout/回调里读最新 chatState（勿在 render 里用 ref 代替 state 画 UI） */
export const useChatStateRefOnly = () => useContext(ChatStateRefContext);
